import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";
import { Flame, Plus, Trash2, Search, RefreshCw, Download, Upload, Pencil } from "lucide-react";

// Firestick Codes Management Component
export const FirestickCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    app_name: "",
    code: "",
    category: "",
    description: "",
    enabled: true
  });
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const fetchCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/firestick-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data);
      // Extract unique categories
      const cats = [...new Set(response.data.map(c => c.category).filter(Boolean))];
      setCategories(cats);
    } catch (error) {
      toast.error("Erreur lors du chargement des codes");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.app_name.trim() || !formData.code.trim()) {
      toast.error("Nom et code sont requis");
      return;
    }

    try {
      if (editingCode) {
        await axios.put(`${API}/admin/firestick-codes/${editingCode.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Code mis à jour");
      } else {
        await axios.post(`${API}/admin/firestick-codes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Code ajouté");
      }
      setDialogOpen(false);
      setEditingCode(null);
      setFormData({ app_name: "", code: "", category: "", description: "", enabled: true });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce code ?")) return;
    try {
      await axios.delete(`${API}/admin/firestick-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Code supprimé");
      fetchCodes();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      app_name: code.app_name,
      code: code.code,
      category: code.category || "",
      description: code.description || "",
      enabled: code.enabled
    });
    setDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/firestick-codes/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `firestick_codes_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API}/admin/firestick-codes/bulk?skip_duplicates=true`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResult(response.data);
      if (response.data.success > 0) {
        toast.success(`${response.data.success} code(s) importé(s)`);
        fetchCodes();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const filteredCodes = codes.filter(code => {
    const matchesSearch = code.app_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         code.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || code.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6 animate-fade-in" data-testid="firestick-management">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Flame className="h-8 w-8 text-orange-500" />
            Firestick Codes
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{codes.length} codes Downloader</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </Button>
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) {
              setSelectedFile(null);
              setImportResult(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Importer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importer des codes</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Format CSV : app_name;code;category;description
                </p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
                <Button onClick={handleImport} disabled={!selectedFile || importing} className="w-full">
                  {importing ? "Import en cours..." : "Importer"}
                </Button>
                {importResult && (
                  <div className="p-3 bg-muted/30 rounded-sm text-sm">
                    <p>✓ {importResult.success} importés</p>
                    <p>○ {importResult.skipped} ignorés</p>
                    {importResult.errors?.length > 0 && (
                      <p className="text-destructive">✗ {importResult.errors.length} erreurs</p>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingCode(null);
              setFormData({ app_name: "", code: "", category: "", description: "", enabled: true });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCode ? "Modifier" : "Nouveau"} code</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nom de l'application *</Label>
                  <Input
                    value={formData.app_name}
                    onChange={(e) => setFormData({...formData, app_name: e.target.value})}
                    placeholder="Ex: Kodi, TiviMate..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code Downloader *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Ex: 12345"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="Ex: IPTV, Streaming, Utilitaire"
                    list="categories-list"
                  />
                  <datalist id="categories-list">
                    {categories.map(cat => <option key={cat} value={cat} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Description optionnelle"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(v) => setFormData({...formData, enabled: v})}
                  />
                  <Label>Actif</Label>
                </div>
                <Button type="submit" className="w-full">
                  {editingCode ? "Mettre à jour" : "Ajouter"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Codes List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredCodes.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Flame className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {codes.length === 0 ? "Aucun code" : "Aucun résultat"}
            </h3>
            <p className="text-muted-foreground">
              {codes.length === 0 
                ? "Ajoutez des codes Firestick pour commencer."
                : "Essayez de modifier votre recherche."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <ScrollArea className="h-[calc(100vh-320px)]">
            <table className="w-full data-table">
              <thead className="sticky top-0 z-10 bg-muted">
                <tr>
                  <th className="text-left py-3 px-4">Application</th>
                  <th className="text-left py-3 px-4">Code</th>
                  <th className="text-left py-3 px-4">Catégorie</th>
                  <th className="text-left py-3 px-4">Description</th>
                  <th className="text-left py-3 px-4">Statut</th>
                  <th className="text-left py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map(code => (
                  <tr key={code.id} className="border-t border-border">
                    <td className="py-3 px-4 font-medium">{code.app_name}</td>
                    <td className="py-3 px-4">
                      <Badge variant="secondary" className="font-mono text-lg">
                        {code.code}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {code.category || "-"}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground max-w-xs truncate">
                      {code.description || "-"}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={code.enabled ? "status-active" : "status-blocked"}>
                        {code.enabled ? "Actif" : "Inactif"}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(code)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(code.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

